import type { Content } from '@common/api/graphql/getContentsGql';
import {
  isGiftCardChoosableTicketConfig,
  isGiftCardFixedTicketConfig
} from '@console/common/project/giftCardTicketConfig';
import ContentRow from '@console/components/common/ContentRow';
import type { LocalGiftCardTicketConfig } from '@console/reducers/projects';
import { Grid } from '@mui/material';
import type { StyleRules, WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import type { ReactElement } from 'react';

const styles = (): StyleRules => ({});

type GiftCardTicketConfigContentsOwnProps = {
  giftCardTicketConfig: LocalGiftCardTicketConfig;
  imageDownloadable?: boolean;
};

type GiftCardTicketConfigContentsProps = GiftCardTicketConfigContentsOwnProps &
  WithStyles<typeof styles>;

const GiftCardTicketConfigContents: React.FC<
  GiftCardTicketConfigContentsProps
> = ({ giftCardTicketConfig, imageDownloadable }) => {
  const renderContent = (
    content: Content,
    availablePeriodText: string,
    selectablePeriodText?: string
  ): ReactElement<any> => (
    <ContentRow
      contentName={content.name}
      contentInternalName={content.internalName}
      contentImageUrl={content.imageUrl}
      contentAvailablePeriodText={availablePeriodText}
      selectablePeriodText={selectablePeriodText}
      contentBrandName={content.brand.name}
      imageDownloadUrl={imageDownloadable ? content.imageUrl : undefined}
    />
  );

  const renderFixedContent = (content): ReactElement<any> =>
    renderContent(content, `有効期限：${content.availablePeriod}`);

  const renderChoosableContent = (content): ReactElement<any> =>
    renderContent(
      content,
      `選択後の有効期限：${content.availablePeriod}`,
      '選択期限：1ヶ月後の月末'
    );

  if (isGiftCardFixedTicketConfig(giftCardTicketConfig)) {
    const { content } = giftCardTicketConfig.fixedContentOption;
    return renderFixedContent(content);
  }

  if (isGiftCardChoosableTicketConfig(giftCardTicketConfig)) {
    return (
      <Grid container direction="column">
        {giftCardTicketConfig.choosableContentOption.choices
          .sort((a, b) => a.order - b.order)
          .map(choice => {
            const { content } = choice;
            return (
              <Grid
                item
                key={`giftCardTicketConfigChoosableContent-${choice.content.urlCode}`}
              >
                {renderChoosableContent(content)}
              </Grid>
            );
          })}
      </Grid>
    );
  }

  return null;
};

export default withStyles(styles)(GiftCardTicketConfigContents);
